import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

const CalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  padding: 30px 0px;
  box-sizing: border-box;
`;

const CalendarGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-start;
  width: 350px; /* 7 cells * 45px + 6 gaps * 2px = 315px + 12px = 327px */
`;

const DayCell = styled.div<{ isCurrentMonth: boolean; isToday: boolean }>`
  position: relative;
  border: 1px solid #ebebeb;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 4px;
  height: 45px;
  width: 45px;
  box-sizing: border-box;
  background-color: ${(props) => (props.isToday ? "#fff4f0" : "#fff")};
  opacity: ${(props) => (props.isCurrentMonth ? 1 : 0.3)};
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.isToday ? "#fff4f0" : "#f9f9f9")};
  }
`;

const DayNumber = styled.span<{ isToday: boolean }>`
  color: ${(props) => (props.isToday ? "#ff734f" : "#232129")};
  font-family: "Sharp Sans";
  font-size: 14px;
  font-weight: ${(props) => (props.isToday ? 700 : 700)};
  line-height: 1;
`;

const ActivityDots = styled.div`
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-top: auto;
  max-width: 100%;
`;

const ActivityDot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #ff734f;
`;

interface Activity {
  date: string; // YYYY-MM-DD format
  count: number;
}

interface CalendarProps {
  activities?: Activity[];
  currentDate?: Date;
}

const Calendar: React.FC<CalendarProps> = ({
  activities = [],
  currentDate = new Date(),
}) => {
  const navigate = useNavigate();
  const today = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getActivityCount = (day: number) => {
    const dateString = `${currentYear}-${String(currentMonth + 1).padStart(
      2,
      "0"
    )}-${String(day).padStart(2, "0")}`;
    const activity = activities.find((a) => a.date === dateString);
    return activity ? activity.count : 0;
  };

  const isToday = (day: number) => {
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth &&
      today.getFullYear() === currentYear
    );
  };

  const handleDayClick = (day: number) => {
    const clickedDate = new Date(currentYear, currentMonth, day);
    const dateString = clickedDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
    navigate(`/agenda/${dateString}`);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const days = [];

    // Only render current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const activityCount = getActivityCount(day);
      const isTodayCell = isToday(day);

      days.push(
        <DayCell
          key={`current-${day}`}
          isCurrentMonth={true}
          isToday={isTodayCell}
          onClick={() => handleDayClick(day)}
        >
          <DayNumber isToday={isTodayCell}>{day}</DayNumber>
          {activityCount > 0 && (
            <ActivityDots>
              {Array.from({ length: Math.min(activityCount, 3) }, (_, i) => (
                <ActivityDot key={i} />
              ))}
              {activityCount > 3 && (
                <span style={{ fontSize: "8px", color: "#ff734f" }}>+</span>
              )}
            </ActivityDots>
          )}
        </DayCell>
      );
    }

    return days;
  };

  return (
    <CalendarContainer>
      <CalendarGrid>{renderCalendarDays()}</CalendarGrid>
    </CalendarContainer>
  );
};

export default Calendar;
